import React, { useMemo, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
} from "@progress/kendo-react-form";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import { ImCross } from "react-icons/im";
import IconButton from "../../components/common/IconButton";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormTextField from "../../components/formFields/FormTextField";
import PurchaseOrderEntryArray from "./PurchaseOrderEntryArray";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";

const PurchaseDetailArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const PurityList = useAppSelector((state) => state.purity.PurityList);
  const purchaseType =
    fieldArrayRenderProps.formRenderProps.valueGetter("PurchaseType");

  const ItemCategoryList = useAppSelector(
    (state) => state.itemCategory?.ItemCategoryList,
  );
  const ProductSubCategoryList = useAppSelector(
    (state) => state.productsubcategory?.ProductSubCategoryList,
  );
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const fieldName = fieldArrayRenderProps.name || "PurchaseForm";

  const pushElementInArray = React.useCallback(() => {
    // Base object for all purchase types
    const baseObject = {
      ProductCategoryID: null,
      PurchaseType: purchaseType,
      ProductSubCategoryID: null,
      ProductID: null,
      Purity: null,
      UniqueProductID: null,
      GrossWeight: null,
      NetWeight: null,
      Wastage: null,
      PhinePayble: null,
      TotalRSPayable: null,
      Remarks: "",
    };

    // Add type-specific fields
    if (purchaseType === "Diamonds") {
      fieldArrayRenderProps?.onPush({
        value: {
          ...baseObject,
          DiamondWeight: null,
          LabelLessWtForDiamond: null,
          DiamondChargesPerCt: null,
          DiamondChargesTotal: null,
          PerCaretDiamondCharges: null,
          OtherStoneWeight: null,
          LabelLessWeightForStone: null,
          OtherChargesChargeBySupplier: null,
          OtherChargesForLable: null,
          LabourPerGram: null,
          TotalLabour: null,
        },
      });
    } else if (purchaseType === "Other Gold Products") {
      fieldArrayRenderProps?.onPush({
        value: {
          ...baseObject,
          LessWeightForLabel: null,
          OtherCharges: null,
          LabourChargesForLabel: null,
        },
      });
    } else if (purchaseType === "Stone Purchase") {
      fieldArrayRenderProps?.onPush({
        value: {
          ...baseObject,
          StoneBagNo: null,
          GMCT: null,
          LessWeight: null,
          RatePerGramCT: null,
        },
      });
    } else if (purchaseType === "Loose Gold Item") {
      fieldArrayRenderProps?.onPush({
        value: {
          ...baseObject,
          BagNo: null,
          LessWeight: null,
          Pcs: null,
        },
      });
    } else {
      fieldArrayRenderProps?.onPush({ value: baseObject });
    }
  }, [fieldArrayRenderProps, purchaseType]);

  const handleDeleteAction = React.useCallback(() => {
    if (deleteIndex !== null && fieldArrayRenderProps?.value[deleteIndex]) {
      fieldArrayRenderProps.onRemove({ index: deleteIndex });
      setDeleteIndex(null);
      dispatch(closeDialog());
    }
  }, [fieldArrayRenderProps, deleteIndex, dispatch]);

  const handleOpenDeleteDialog = (index: number) => {
    setDeleteIndex(index);
    dispatch(openDialog("Purchase-Item-Delete-Dialog"));
  };

  const filterSubCategory = (ItemCategoryID: number) => {
    if (!ProductSubCategoryList) return [];
    const result = ProductSubCategoryList.filter((item: any) => {
      return ItemCategoryID ? item.item_category_id === ItemCategoryID : true;
    });
    return result.map((item: any) => ({
      value: item?.id,
      label: item?.item_subcategory_name,
    }));
  };

  const filterItems = (
    ItemCategoryID: number,
    ProductSubCategoryID: number,
  ) => {
    if (!ItemList) return [];
    const result = ItemList.filter((item: any) => {
      return (
        (ItemCategoryID ? item.item_category_id === ItemCategoryID : true) &&
        (ProductSubCategoryID
          ? item.item_sub_category_id === ProductSubCategoryID
          : true)
      );
    });
    return result.map((item: any) => ({
      value: item?.id,
      label: item?.item_name,
    }));
  };

  const getProductName = (productId: number) => {
    return (
      ItemList?.find((prod: any) => prod.id === productId)?.item_name || "-"
    );
  };

  // const handleChangeLabout = ({
  //   formRenderProps,
  //   index,
  // }: {
  //   formRenderProps: FormRenderProps;
  //   index: number;
  // }) => {
  //   const LabourPerGram =
  //     +formRenderProps.valueGetter(`PurchaseForm.${index}.LabourPerGram`) || 0;
  //   const NetWeight =
  //     +formRenderProps.valueGetter(`PurchaseForm.${index}.NetWeight`) || 0;
  //   let TotalLabour = NetWeight * LabourPerGram;
  //   if (LabourPerGram && NetWeight) {
  //     formRenderProps.onChange(`PurchaseForm.${index}.TotalLabour`, {
  //       value: TotalLabour.toFixed(3),
  //     });
  //   }
  // };

  const filteredValue = useMemo(
    () =>
      fieldArrayRenderProps.value
        ?.map((item: any, index: number) => ({ ...item, originalIndex: index }))
        .filter(
          (item: any) =>
            item?.PurchaseType ===
            fieldArrayRenderProps.formRenderProps.valueGetter("PurchaseType"),
        ),
    [
      fieldArrayRenderProps.value,
      fieldArrayRenderProps.formRenderProps.valueGetter("PurchaseType"),
    ],
  );

  // Render row fields (always editable)
  const renderRow = (index: number) => {
    const baseFields = (
      <>
        <td>
          <Field
            wrapperStyle={{ margin: 0, width: "100%" }}
            name={`${fieldName}.${index}.ProductCategoryID`}
            placeholder="Select Category"
            component={FormSelectionField}
            options={ItemCategoryList?.map((item) => ({
              value: item.ID,
              label: item.category_name,
            }))}
            validator={requiredValidator}
          />
        </td>
        <td>
          <Field
            wrapperStyle={{ margin: 0, width: "100%" }}
            name={`${fieldName}.${index}.ProductSubCategoryID`}
            placeholder="Select Sub Category"
            component={FormSelectionField}
            validator={requiredValidator}
            options={
              ProductSubCategoryList &&
              filterSubCategory(
                fieldArrayRenderProps.formRenderProps.valueGetter(
                  `${fieldName}.${index}.ProductCategoryID`,
                ),
              )
            }
          />
        </td>
      </>
    );

    if (purchaseType === "Diamonds") {
      return (
        <>
          {baseFields}
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.UniqueProductID`}
              placeholder="Unique Product ID"
              disabled={true}
              component={FormTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Purity`}
              type="number"
              placeholder="Select Purity"
              component={FormSelectionField}
              options={PurityList?.map((purity: any) => {
                return {
                  value: purity.ID,
                  label: `${purity.Carat}-${purity.purity}`,
                };
              })}
              onChange={() =>
                fieldArrayRenderProps.handleChangePayblePhine({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
              validator={requiredValidator}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.GrossWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              validator={requiredValidator}
              onChange={() =>
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.DiamondWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LabelLessWtForDiamond`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.WeightCarat`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.DiamondChargesPerCt`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.DiamondChargesTotal`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
              disabled={true}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.PerCaretDiamondCharges`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.OtherStoneWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LabelLessWeightForStone`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.OtherChargesChargeBySupplier`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.OtherChargesForLable`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.NetWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              disabled={true}
              onChange={() => {
                fieldArrayRenderProps.handleChangePayblePhine({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                });
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index,
                });
              }}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Wastage`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangePayblePhine({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LabourPerGram`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeDiamondField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LabourChargesForLabel`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.TotalLabour`}
              disabled={true}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.PhinePayble`}
              disabled={true}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.TotalRSPayable`}
              placeholder="0.000"
              disabled={true}
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Remarks`}
              placeholder="Remarks"
              component={FormTextArea}
              rows={1}
            />
          </td>
        </>
      );
    } else if (purchaseType === "Other Gold Products") {
      return (
        <>
          {baseFields}
          <td>
            <Field
              wrapperStyle={{ margin: 0, width: "100%" }}
              name={`${fieldName}.${index}.ProductID`}
              placeholder="Select Product"
              component={FormSelectionField}
              validator={requiredValidator}
              options={
                ItemList &&
                filterItems(
                  fieldArrayRenderProps.formRenderProps.valueGetter(
                    `${fieldName}.${index}.ProductCategoryID`,
                  ),
                  fieldArrayRenderProps.formRenderProps.valueGetter(
                    `${fieldName}.${index}.ProductSubCategoryID`,
                  ),
                )
              }
              onChange={(e) =>
                fieldArrayRenderProps.handleSelectProductInArray({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                  e,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.UniqueProductID`}
              placeholder="Unique Product ID"
              disabled={true}
              component={FormTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Purity`}
              type="number"
              placeholder="Select Purity"
              component={FormSelectionField}
              options={PurityList?.map((purity: any) => {
                return {
                  value: purity.ID,
                  label: `${purity.Carat}-${purity.purity}`,
                };
              })}
              onChange={() =>
                fieldArrayRenderProps.handleChangePayblePhine({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
              validator={requiredValidator}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.GrossWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              validator={requiredValidator}
              onChange={() =>
                fieldArrayRenderProps.handleChangeGoldField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LessWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeGoldField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LessWeightForLabel`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.NetWeight`}
              placeholder="0.000"
              format="n3"
              disabled={true}
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeGoldField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Wastage`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangePayblePhine({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.PhinePayble`}
              disabled={true}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.TotalRSPayable`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LabourChargesForLabel`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Remarks`}
              placeholder="Remarks"
              component={FormTextArea}
              rows={1}
            />
          </td>
        </>
      );
    } else if (purchaseType === "Stone Purchase") {
      return (
        <>
          {baseFields}
          <td>
            <Field
              wrapperStyle={{ margin: 0, width: "100%" }}
              name={`${fieldName}.${index}.ProductID`}
              placeholder="Select Product"
              component={FormSelectionField}
              validator={requiredValidator}
              options={
                ItemList &&
                filterItems(
                  fieldArrayRenderProps.formRenderProps.valueGetter(
                    `${fieldName}.${index}.ProductCategoryID`,
                  ),
                  fieldArrayRenderProps.formRenderProps.valueGetter(
                    `${fieldName}.${index}.ProductSubCategoryID`,
                  ),
                )
              }
              onChange={(e) =>
                fieldArrayRenderProps.handleSelectProductInArray({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                  e,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.UniqueProductID`}
              placeholder="Unique Product ID"
              disabled={true}
              component={FormTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.StoneBagNo`}
              placeholder="Bag No"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.GMCT`}
              placeholder="Select Unit"
              component={FormSelectionField}
              options={[
                { label: "Gram", value: "gm" },
                { label: "Carat", value: "ct" },
              ]}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.GrossWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              validator={requiredValidator}
              onChange={() =>
                fieldArrayRenderProps.handleChangeStoneField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LessWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangeStoneField({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.NetWeight`}
              placeholder="0.000"
              format="n3"
              disabled={true}
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.RatePerGramCT`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.TotalRSPayable`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Remarks`}
              placeholder="Remarks"
              component={FormTextArea}
              rows={1}
            />
          </td>
        </>
      );
    } else if (purchaseType === "Loose Gold Item") {
      const handleLooseGoldWeightChange = () => {
        const GrossWeight =
          +fieldArrayRenderProps.formRenderProps.valueGetter(
            `${fieldName}.${index}.GrossWeight`,
          ) || 0;
        const LessWeight =
          +fieldArrayRenderProps.formRenderProps.valueGetter(
            `${fieldName}.${index}.LessWeight`,
          ) || 0;
        const NetWeight = GrossWeight - LessWeight;

        fieldArrayRenderProps.formRenderProps.onChange(
          `${fieldName}.${index}.NetWeight`,
          { value: NetWeight.toFixed(3) },
        );

        // Also trigger Phine Payable change
        setTimeout(() => {
          fieldArrayRenderProps.handleChangePayblePhine({
            formRenderProps: fieldArrayRenderProps.formRenderProps,
            index: index,
          });
        }, 100);
      };

      return (
        <>
          {baseFields}
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.BagNo`}
              placeholder="Bag No"
              component={FormTextField}
              disabled={true}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Purity`}
              type="number"
              placeholder="Select Purity"
              component={FormSelectionField}
              options={PurityList?.map((purity: any) => {
                return {
                  value: purity.ID,
                  label: `${purity.Carat}-${purity.purity}`,
                };
              })}
              validator={requiredValidator}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.GrossWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              validator={requiredValidator}
              onChange={handleLooseGoldWeightChange}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.LessWeight`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
              onChange={handleLooseGoldWeightChange}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.NetWeight`}
              placeholder="0.000"
              format="n3"
              disabled={true}
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.PCS`}
              placeholder="0"
              type="number"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Wastage`}
              placeholder="0.00"
              format="n2"
              component={FormNumericTextField}
              onChange={() =>
                fieldArrayRenderProps.handleChangePayblePhine({
                  formRenderProps: fieldArrayRenderProps.formRenderProps,
                  index: index,
                })
              }
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.PhinePayble`}
              disabled={true}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.TotalRSPayable`}
              placeholder="0.000"
              format="n3"
              component={FormNumericTextField}
            />
          </td>
          <td>
            <Field
              wrapperStyle={{ margin: 0 }}
              name={`${fieldName}.${index}.Remarks`}
              placeholder="Remarks"
              component={FormTextArea}
              rows={1}
            />
          </td>
        </>
      );
    }
    return (
      <>
        {baseFields}
        <td>
          <Field
            wrapperStyle={{ margin: 0 }}
            name={`${fieldName}.${index}.Remarks`}
            placeholder="Remarks"
            component={FormTextArea}
            rows={1}
          />
        </td>
      </>
    );
  };

  // Render table headers based on purchase type
  const renderTableHeaders = () => {
    const baseHeaders = ["Category", "Sub Category"];

    if (purchaseType === "Diamonds") {
      return [
        ...baseHeaders,
        "Unique Product ID",
        "Purity",
        "Gross Weight (gm)",
        "Diamond Weight (Gm) (If Less Given By Supplier)",
        "Label Less Weight for Diamond",
        "Diamond Weight (Carat)",
        "Diamond Charges Per Caret",
        "Diamond Charges Total",
        "Per Caret Diamond Charges for Label",
        "Other Stone Weight",
        "Label Less Weight For Other Stone",
        "Other Charges By Supplier",
        "Other Charges For Label",
        "Net Weight (gm)",
        "Wastage",
        "Labour Per Gram",
        "Labour Charges For Label (%)",
        "Total Labour",
        "Phine Payble",
        "Total Rs Payble",
        "Remarks",
      ];
    } else if (purchaseType === "Other Gold Products") {
      return [
        ...baseHeaders,
        "Product",
        "Unique Product ID",
        "Purity",
        "Gross Weight (gm)",
        "Less Weight (gm)",
        "Less Weight For Label (gm)",
        "Net Weight (gm)",
        "Wastage",
        "Phine Payble",
        "Total Rs Payble",
        "Labour Charges For Label (%)",
        "Remarks",
      ];
    } else if (purchaseType === "Stone Purchase") {
      return [
        ...baseHeaders,
        "Product",
        "Unique Product ID",
        "Stone Bag No",
        "GM/CT",
        "Gross Weight",
        "Less Weight",
        "Net Weight",
        "Rate",
        "Total Rs Payble",
        "Remarks",
      ];
    } else if (purchaseType === "Loose Gold Item") {
      return [
        ...baseHeaders,
        "Bag No",
        "Purity",
        "Gross Weight (gm)",
        "Less Weight (gm)",
        "Net Weight (gm)",
        "Pcs",
        "Wastage",
        "Phine Payble",
        "Total Rs Payble",
        "Remarks",
      ];
    }
    return [...baseHeaders, "Remarks"];
  };

  return (
    <>
      {/* Delete Confirmation Dialog */}
      {dialogName === "Purchase-Item-Delete-Dialog" && (
        <AppDialog>
          <div style={{ width: "30vw" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography.h4 style={{ margin: 0 }}>
                Delete Purchase
              </Typography.h4>
              <ImCross
                style={{
                  color: "white",
                  background: "red",
                  borderRadius: "50%",
                  padding: "5px",
                  fontSize: "24px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  dispatch(closeDialog());
                  setDeleteIndex(null);
                }}
              />
            </div>
            <Typography.p>
              Are you sure you want to delete this purchase?
            </Typography.p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <DialogCloseButton themeColor="error" />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={handleDeleteAction}
                type="button"
              >
                Delete
              </Button>
            </div>
          </div>
        </AppDialog>
      )}

      {/* Header with Add Button */}
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Typography.h5 style={{ marginBottom: 16 }}>
              {purchaseType === "Diamonds"
                ? "Diamond Purchase List"
                : purchaseType === "Other Gold Products"
                  ? "Other Gold Purchase Detail"
                  : purchaseType === "Stone Purchase"
                    ? "Stone Purchase Detail"
                    : "Purchase Purchase List"}
            </Typography.h5>
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInArray}
              type="button"
            >
              Add Item
            </Button>
          </div>
        </GridLayoutItem>
      </GridLayout>

      {/* Table */}
      {filteredValue && filteredValue.length > 0 ? (
        <div
          className="table-responsive"
          style={{
            maxHeight: "50vh",
            scrollbarWidth: "thin",
            overflowX: "auto",
          }}
        >
          <table
            className="table table-striped table-bordered table-hover field-array-table"
            style={{
              minWidth:
                purchaseType === "Diamonds"
                  ? 5500
                  : purchaseType === "Loose Gold Item"
                    ? 2200
                    : 2800,
            }}
          >
            <thead>
              <tr>
                {renderTableHeaders().map((header, idx) => (
                  <th
                    key={idx}
                    style={{
                      width: header === "Actions" ? "5%" : "auto",
                      verticalAlign: "middle",
                    }}
                  >
                    {header}
                  </th>
                ))}
                <th
                  style={{
                    width: "2%",
                    position: "sticky",
                    right: 0,
                    zIndex: 10,
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {filteredValue?.map((item: any) => (
                <tr key={item.originalIndex}>
                  {renderRow(item.originalIndex)}
                  <td
                    style={{
                      position: "sticky",
                      right: 0,
                      zIndex: 2,
                      backgroundColor: "#fff",
                      textAlign: "center",
                    }}
                  >
                    <IconButton
                      type="button"
                      fillMode="flat"
                      size="small"
                      title="Delete"
                      themeColor="error"
                      onClick={() => handleOpenDeleteDialog(item.originalIndex)}
                      style={{
                        height: 32,
                        width: 32,
                      }}
                    >
                      <MdDelete style={{ fontSize: "20px" }} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
          <Typography.p>
            No items added. Click "Add Item" to add items.
          </Typography.p>
        </div>
      )}

      {/* Nested Purchase Items Section - Only for Antique type */}
      {purchaseType === "Antique" &&
        filteredValue &&
        filteredValue.length > 0 && (
          <div
            style={{
              marginTop: "24px",
            }}
          >
            {filteredValue.map(
              (item: any, index: number) =>
                item?.ProductID && (
                  <div
                    key={index}
                    style={{
                      marginTop: index > 0 ? "24px" : 0,
                      border: "1px solid #d9d9d9",
                      borderRadius: "8px",
                      padding: "16px",
                    }}
                  >
                    <Typography.h6 style={{ marginBottom: 12 }}>
                      Child Items for {getProductName(item?.ProductID)}
                    </Typography.h6>
                    <FieldArray
                      formRenderProps={fieldArrayRenderProps.formRenderProps}
                      component={PurchaseOrderEntryArray}
                      name={`${fieldName}.${index}.PurchaseFormChild`}
                    />
                  </div>
                ),
            )}
          </div>
        )}
    </>
  );
};

export default PurchaseDetailArray;
